// src/components/UploadTableSingle.tsx
import React, { useState } from "react";
import UploadField from "../../components/UploadField";
import AttachmentEmptyState from "./AttachmentEmptyState";

type UploadTableSingleProps = {
    title?: string;
    value?: string;
    onChange: (url: string) => void;
    readOnly?: boolean;
};

const UploadTableSingle: React.FC<UploadTableSingleProps> = ({
    title,
    value,
    onChange,
    readOnly
}) => {

    const [pendingFile, setPendingFile] = useState<File | null>(null);

    if (readOnly && !value?.trim() && !pendingFile) {
        return <AttachmentEmptyState />;
    }

    const handleUploadClick = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "application/pdf,image/*";

        input.onchange = (e: any) => {
            const picked = e.target.files?.[0];
            if (!picked) return;
            setPendingFile(picked);
        };

        input.click();
    };

    return (
        <div className="border rounded-lg p-3 bg-white shadow-sm">

            {/* Hidden UploadField to handle upload */}
            {pendingFile && (
                <div className="hidden">
                    <UploadField
                        label=""
                        value=""
                        mode="file"
                        initialFile={pendingFile}
                        onChange={(url: string) => {
                            onChange(url);
                            setPendingFile(null);
                        }}
                    />
                </div>
            )}

            {/* Header — ALWAYS visible */}
            <div className="grid grid-cols-3 text-[10px] font-semibold border-b pb-2">
                <span>File Name</span>
                <span className="text-center">View File</span>
                <span className="text-center">Remove File</span>
            </div>

            {/* Row — ONLY if file exists */}
            {value && (
                <div className="grid grid-cols-3 items-center py-2 border-b">

                    <span className="text-[10px]">Document</span>

                    <div className="flex justify-center">
                        <a
                            href={value}
                            target="_blank"
                            className="border p-1 rounded-md"
                        >
                            ↗
                        </a>
                    </div>

                    <div className="flex justify-center">
                        {!readOnly ? (
                            <button
                                onClick={() => onChange("")}
                                className="border p-1 rounded-md text-xs"
                            >
                                🗑
                            </button>
                        ) : (
                            <span className="text-text-subtle">—</span>
                        )}
                    </div>

                    {/* Replace upload */}
                    {!readOnly && (
                        <div className="col-span-3 mt-2">
                            <UploadField
                                label=""
                                value={value}
                                onChange={(newUrl: string) => onChange(newUrl)}
                                mode="file"
                                accept="application/pdf,image/*"
                                forceUploadOnly={true}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Loader */}
            {pendingFile && (
                <div className="grid grid-cols-3 items-center py-2 animate-pulse">
                    <span className="text-[10px] text-text-subtle">Uploading…</span>
                    <div className="flex justify-center">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <div className="flex justify-center">
                        <span className="text-text-subtle text-[10px]">—</span>
                    </div>
                </div>
            )}

            {/* Upload button — ONLY when empty */}
            {/* Upload button — ONLY when empty */}
            {!readOnly && !value && (
                <div className="flex justify-center items-center">
                    <button
                        onClick={handleUploadClick}
                        disabled={!!pendingFile}
                        className={`mt-3 w-full px-3 font-bold border rounded-md py-2 text-[10px]
                ${pendingFile
                                ? "bg-bg-button-primary/60 cursor-not-allowed"
                                : "bg-bg-button-primary"
                            }`}
                    >
                        {pendingFile ? "UPLOADING..." : "+ UPLOAD FILE"}
                    </button>
                </div>
            )}

        </div>
    );
};

export default UploadTableSingle;
